import * as Sentry from '@sentry/react';

// `addons.css_name` (free text in /admin/addons) selects WHICH preview the
// Announcements & Gifts screens draw for a product. Both renderers -- the grid
// card (Holiday/Builder/Item) and the builder/edit sheet (MultiPose/
// DisplayableSheet) -- switch on a CLOSED set of variants, and an unrecognised
// value used to fall through to `default: return null`. The product then
// rendered as an EMPTY BOX on a live sales page: no preview, and (visually)
// nothing to click. "(1) 8x10 Senior Portrait" shipped that way, carrying the
// print-template code `T810` in css_name.
//
// The renderers now draw the add-on's own product image for an unknown variant,
// so a misconfigured product is always visible and buyable, and report it here
// so the misconfiguration surfaces instead of silently costing sales.
//
// One implementation shared by both renderers -- previously each carried its own
// silent `return null`, so a fix to one would not have reached the other.

const reported = new Set();

export default function reportUnknownAddonVariant(addon, variant) {
  try {
    const cssName = variant || '(blank)';

    // Once per css_name per pageview: a rate, not a flood (the grid re-renders
    // on every pose/background rotation).
    if (reported.has(cssName)) return;
    reported.add(cssName);

    // eslint-disable-next-line no-console
    console.warn(
      `Add-on "${addon?.name}" has css_name "${cssName}", which no preview knows how to draw — `
      + 'falling back to the product image with no pose overlay. Set css_name to a supported '
      + 'variant in /admin/addons.',
    );

    Sentry.captureMessage('addon:unknown-preview-variant', {
      level: 'warning',
      tags: { addonVariant: cssName },
      extra: { addonId: addon?.id, addonName: addon?.name, cssName },
    });
  } catch (_) { /* reporting must never break a sales page */ }
}

// Test seam: the once-per-variant dedupe is module state.
export function resetUnknownAddonVariantReports() {
  reported.clear();
}
